<template>
  <div class="rel" :key="'leaf_' + identifierRoot.padEnd(5, '0')">
    <div v-for="(item, i) in items" :key="parent + '-' + item.id" class="ml-5">
      <div class="d-flex align-center">
        <v-btn
          @click="item.children ? changeOpen(item.id) : null"
          icon class="rel"
          :disabled="opened.includes(item.id) && !item.children"
        >
          <IconsBaseCheckbox
            :icon="opened.includes(item.id) && item.children ? 'minus' : item.children ? 'plus' : 'blank'"
            :width="20" :height="20" color="#277FAF"
          />
          <div class="abs1"/>
        </v-btn>
        <template v-if="item.id === leaf.id">
          <v-sheet width="100%" max-width="500" class="mr-2">
            <v-text-field v-model="leaf.name" flat outlined hide-details autofocus>
              <template slot="prepend-inner">
                <div class="d-flex align-center ml-n2">
                  <v-icon color="#277FAF" class="mr-1" size="18">mdi-file-outline</v-icon>
                  <div class="colored">{{ String(identifier + i + 1).padEnd(5, '0') }}</div>
                </div>
              </template>
            </v-text-field>
          </v-sheet>
          <v-hover v-slot="{ hover }">
            <v-btn @click="saveLeaf(item.id)" icon :disabled="leaf.name.length === 0">
              <v-icon color="#277FAF">
                <template v-if="hover">mdi-check-circle</template>
                <template v-else>mdi-check-circle-outline</template>
              </v-icon>
            </v-btn>
          </v-hover>
          <v-hover v-slot="{ hover }">
            <v-btn @click="cancelChanges(item.id === -1)" icon>
              <v-icon color="red">
                <template v-if="hover">mdi-close-circle</template>
                <template v-else>mdi-close-circle-outline</template>
              </v-icon>
            </v-btn>
          </v-hover>
        </template>
        <v-hover v-else v-slot="{ hover }">
          <div class="d-flex align-center">
            <v-icon color="#277FAF" class="mr-1" size="18">
              <template v-if="item.children">mdi-folder</template>
              <template v-else>mdi-file-outline</template>
            </v-icon>
            <span class="colored mr-1">{{ String(identifier + i + 1).padEnd(5, '0') }}</span>
            <span class="mr-2">{{ item.name }}</span>
            <template v-if="hover">
              <v-btn icon v-if="!(identifierRoot.length > 4) && !item.children" @click="addChild(i)">
                <v-icon color="#277FAF" size="24">mdi-file-plus</v-icon>
              </v-btn>
              <v-btn @click="openEdit(item)" icon>
                <v-icon color="#277FAF" size="24" style="margin: 2px 0 -2px">mdi-border-color</v-icon>
              </v-btn>
              <v-btn @click="deleteItem(item)" icon>
                <IconsButton icon="button-delete" class="mt-n1"/>
              </v-btn>
            </template>
          </div>
        </v-hover>
      </div>
      <TreeViewItem
        v-if="opened.includes(item.id) && item.children"
        :items="item.children"
        :parent="item.id"
        :identifier="(identifier + i + 1) * 10"
        :opened="opened"
        :on-change-open="onChangeOpen"
        :on-create="onCreate"
        :on-save="onSave"
        :on-delete="onDelete"
        :refresh-tree="refreshTree"
        :get-items="getItems"
      />
    </div>
    <div class="ml-5 d-flex align-center">
      <v-btn @click="leaf.create = true" icon class="rel" :disabled="leaf.edit || items.length === 9">
        <IconsBaseCheckbox
          :icon="leaf.edit ? 'blank' : 'plus-outlined'"
          :width="20" :height="20"
          :color="items.length === 9 ? '#d9d9d9' : '#277FAF'"
        />
        <div class="abs1"/>
      </v-btn>
      <template v-if="leaf.create">
        <v-sheet width="100%" max-width="500" class="mr-2">
          <v-text-field v-model="leaf.name" flat outlined hide-details autofocus>
            <template slot="prepend-inner">
              <div class="d-flex align-center ml-n2">
                <v-icon color="#277FAF" class="mr-1" size="18">mdi-file-outline</v-icon>
                <div class="colored">{{ identifierRoot.padEnd(5, '0') }}</div>
              </div>
            </template>
          </v-text-field>
        </v-sheet>
        <v-hover v-slot="{ hover }">
          <v-btn @click="createLeaf" icon :disabled="leaf.name.length === 0">
            <v-icon color="#277FAF">
              <template v-if="hover">mdi-check-circle</template>
              <template v-else>mdi-check-circle-outline</template>
            </v-icon>
          </v-btn>
        </v-hover>
        <v-hover v-slot="{ hover }">
          <v-btn @click="clearLeaf" icon>
            <v-icon color="red">
              <template v-if="hover">mdi-close-circle</template>
              <template v-else>mdi-close-circle-outline</template>
            </v-icon>
          </v-btn>
        </v-hover>
      </template>
    </div>
    <div :class="parent !== null ? 'abs' : 'abs0'"/>
  </div>
</template>

<script>
import TreeViewItem from "components/base/treeview/TreeViewItem";
import IconsBaseCheckbox from "../../icons/base/IconsBaseCheckbox";
import IconsButton from "../../icons/button/IconsButton";

export default {
  name: "TreeViewRoot",
  components: {IconsButton, IconsBaseCheckbox, TreeViewItem},
  props: {
    items: {
      type: Array,
      default: () => {
        return []
      }
    },
    parent: {
      type: [Number, String],
      default: null
    },
    identifier: {
      type: Number,
      default: 0
    },
    opened: {
      type: Array,
      default: () => {
        return []
      }
    },
    onChangeOpen: {},
    onCreate: {},
    onSave: {},
    onDelete: {},
    refreshTree: {},
    getItems: {},
  },
  data: () => ({
    leaf: {
      create: false,
      name: '',
      id: -1,
      parent: -1,
    },
  }),
  computed: {
    identifierRoot() {
      return String(this.identifier + this.items.length + 1)
    }
  },
  methods: {
    changeOpen(id) {
      if (this.onChangeOpen) this.onChangeOpen(id)
    },
    clearLeaf() {
      this.leaf.create = false
      this.leaf.name = ''
      this.leaf.id = -1
      this.leaf.parent = -1
      if (this.refreshTree) this.refreshTree()
    },
    createLeaf() {
      if (this.onCreate) this.onCreate(this.parent, this.leaf.name)
      this.getItems()
      this.clearLeaf()
    },
    openEdit(item) {
      this.leaf.name = item.name
      this.leaf.id = item.id
    },
    addChild(i) {
      this.items[i].children = [{id: -1}]
      if (this.refreshTree) this.refreshTree()
      this.leaf.parent = this.items[i].id
    },
    saveLeaf(id) {
      if (id === -1) this.createLeaf()
      else if (this.onSave) this.onSave(this.leaf.id, this.leaf.name)
      this.clearLeaf()
    },
    cancelChanges(clearChildren) {
      if (clearChildren && this.getItems) {
        this.getItems()
      }
      this.clearLeaf()
    },
    deleteItem(item) {
      if (this.onDelete) this.onDelete(item)
    }
  }
}
</script>

<style scoped lang="scss">
.colored {
  color: #277FAF;
  cursor: default;
}

.rel {
  position: relative;
}

.abs {
  position: absolute;
  top: -8px;
  left: 17px;
  width: 2px;
  height: calc(100% - 10px);
  border-left: 1px dashed #277FAF;
}

.abs0 {
  position: absolute;
  top: 12px;
  left: 17px;
  width: 2px;
  height: calc(100% - 30px);
  border-left: 1px dashed #277FAF;
}

.abs1 {
  position: absolute;
  top: 11px;
  left: 0;
  width: 8px;
  height: 4px;
  border-top: 1px dashed #277FAF;
}

:deep() .v-input.v-text-field .v-input__slot:not(.v-textarea) {
  min-height: 30px !important;
}
</style>
